import { useState, useCallback, useRef } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import type { User } from '../../common/types.tsx';

export type FetchUsers = (query?: string) => Promise<User[] | undefined>;

const useUsersQuery = (
	autoCompleteUrl: string,
	customFetchUsers?: FetchUsers,
): [
	{
		data: User[];
		error?: Error;
		loading: boolean;
	},
	FetchUsers,
] => {
	const [users, setUsers] = useState<User[]>([]);
	const [error, setError] = useState<Error | undefined>(undefined);
	const [loading, setLoading] = useState<boolean>(false);

	const currentQuery = useRef('');

	const fetchUsers = useCallback<FetchUsers>(
		// @ts-expect-error - TS2345 - Argument of type '(query?: string | undefined) => Promise<void> | Promise<never[]>' is not assignable to parameter of type 'FetchUsers'.
		(query = '') => {
			if (['', null, undefined].includes(autoCompleteUrl)) {
				return Promise.resolve([]);
			}

			setLoading(true);
			currentQuery.current = query;

			if (error) {
				setError(undefined);
			}

			const usersFetchUrl = `${autoCompleteUrl || ''}${query}`;

			const fetchUsersMethod =
				customFetchUsers && fg('issue_cards_in_program_board')
					? customFetchUsers(query)
					: fetchJson(usersFetchUrl);

			return fetchUsersMethod
				.then((response: User[]) => {
					if (query === currentQuery.current) {
						setUsers(response);
					}
				})
				.catch((err: Error) => {
					if (query === currentQuery.current) {
						setError(err);
					}
					const errorMessage =
						customFetchUsers && fg('issue_cards_in_program_board')
							? 'Failed to fetch using custom user fetcher'
							: 'Failed to fetch users';
					log.safeErrorWithoutCustomerData('issue.fields.assigne', errorMessage, err);
				})
				.finally(() => setLoading(false));
		},
		[autoCompleteUrl, error, customFetchUsers],
	);

	return [{ data: users, error, loading }, fetchUsers];
};

export default useUsersQuery;
